
import React from "react";
import { LicenseVariables } from "@/types/licensing";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLicenseTextTemplate } from "@/utils/pdf/licenseTextTemplates";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Clock, Monitor, DollarSign, Bot, Palette, Database, Trophy, Settings } from "lucide-react";

interface FullLicenseTextProps {
  licenseVariables: LicenseVariables;
  permissions?: string[];
  restrictions?: string[];
  condensed?: boolean;
  showConfiguration?: boolean;
}

export const FullLicenseText: React.FC<FullLicenseTextProps> = ({
  licenseVariables,
  permissions = [],
  restrictions = [],
  condensed = false,
  showConfiguration = false
}) => {
  const { language } = useLanguage();

  // Debug logging to track license variables
  console.log("=== FULL LICENSE TEXT DEBUG: Received license variables:", licenseVariables);
  console.log("=== FULL LICENSE TEXT DEBUG: Boolean values:", {
    native_monetization_allowed: licenseVariables.native_monetization_allowed,
    ai_editing_tools_allowed: licenseVariables.ai_editing_tools_allowed,
    ai_visual_transformation_allowed: licenseVariables.ai_visual_transformation_allowed,
    ai_training_database_allowed: licenseVariables.ai_training_database_allowed,
    challenge_participation: licenseVariables.challenge_participation
  });

  // Get the full license text using the updated template
  const fullLicenseHtml = getLicenseTextTemplate(licenseVariables, permissions, restrictions, language);

  // Remove the unwanted title from the license text
  const cleanedLicenseHtml = fullLicenseHtml.replace(/📄\s*CONTRAT DE LICENCE RECUT/gi, '');

  // Remove the License Conditions Summary section entirely - improved regex for all French variants
  const finalLicenseHtml = cleanedLicenseHtml.replace(/📋\s*(License Conditions Summary|Résumé des Conditions de Licence|SYNTHÈSE DES CONDITIONS DE LICENCE)[\s\S]*?(?=<h[1-6]|📄|📋|$)/gi, '');

  // Format the effective date
  const currentDate = new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const effectiveDateText = language === 'en' 
    ? `Effective Date: ${currentDate}`
    : `Date d'entrée en vigueur : ${currentDate}`;

  return (
    <div className="space-y-4">
      {showConfiguration}
      
      {/* Effective Date Section */}
      <div className="border-b border-border pb-4 mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span className="font-medium">{effectiveDateText}</span>
        </div>
      </div>
      
      <div className={`prose prose-sm max-w-none leading-relaxed ${condensed ? 'text-xs' : 'text-sm'}`} 
           style={{
             '--tw-prose-headings': 'hsl(var(--foreground))',
             '--tw-prose-h2': 'hsl(var(--foreground))',
             '--tw-prose-h3': 'hsl(var(--foreground))',
             '--tw-prose-h4': 'hsl(var(--foreground))',
             '--tw-prose-h5': 'hsl(var(--foreground))',
             '--tw-prose-body': 'hsl(var(--muted-foreground))',
             '--tw-prose-bold': 'hsl(var(--foreground))',
             '--tw-prose-strong': 'hsl(var(--foreground))'
           } as React.CSSProperties} 
           dangerouslySetInnerHTML={{ __html: finalLicenseHtml }} />
      
      <style dangerouslySetInnerHTML={{
        __html: `
          .prose h2 {
            font-size: 1.25rem !important;
            font-weight: 700 !important;
            color: hsl(var(--foreground)) !important;
            margin-top: 1.5rem !important;
            margin-bottom: 0.75rem !important;
            border-bottom: 1px solid hsl(var(--border)) !important;
            padding-bottom: 0.5rem !important;
          }
          
          .prose h3 {
            font-size: 1.125rem !important;
            font-weight: 600 !important;
            color: hsl(var(--foreground)) !important;
            margin-top: 1.25rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .prose h4 {
            font-size: 1rem !important;
            font-weight: 600 !important;
            color: hsl(var(--foreground)) !important;
            margin-top: 1rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .prose h5 {
            font-size: 0.875rem !important;
            font-weight: 600 !important;
            color: hsl(var(--foreground)) !important;
            margin-top: 0.75rem !important;
            margin-bottom: 0.25rem !important;
          }
          
          .prose strong {
            font-weight: 600 !important;
            color: hsl(var(--foreground)) !important;
          }
          
          .prose p {
            margin-bottom: 0.75rem !important;
            line-height: 1.6 !important;
          }
          
          .prose ul, .prose ol {
            margin-top: 0.5rem !important;
            margin-bottom: 0.75rem !important;
          }
          
          .prose li {
            margin-bottom: 0.25rem !important;
          }
        `
      }} />
    </div>
  );
};
